import type { DocumentEmailType, DocumentMailContentType } from '@econtractor/types';
import i18n from 'i18next';
import { escapeHtml } from '../functions/escapeHtml';

const EMAIL_BODY_MAX_LENGTH = 1000;
const DOCUMENT_TYPES = new Set<DocumentEmailType>([
    'proposal',
    'contract',
    'invoice',
    'act',
    'other',
]);

const ensureEmailNamespace = async (): Promise<void> => {
    if (!i18n.hasResourceBundle(i18n.language, 'email')) {
        await i18n.loadNamespaces('email');
    }
};

const tEmail = (key: string, options?: Record<string, unknown>): string =>
    String(i18n.t(key, { ns: 'email', ...(options || {}) }));

const resolveDocumentType = (value?: string | null): DocumentEmailType => {
    const normalized = String(value || '')
        .trim()
        .toLowerCase();
    if (DOCUMENT_TYPES.has(normalized as DocumentEmailType)) {
        return normalized as DocumentEmailType;
    }
    return 'other';
};

const buildEmailBodyBlock = (emailBody?: string | null): string => {
    const text = String(emailBody || '').trim().slice(0, EMAIL_BODY_MAX_LENGTH);
    if (!text) return '';
    return `<p style="margin: 0 0 12px; white-space: pre-wrap;">${escapeHtml(text)}</p>`;
};

const buildMailShell = (params: {
    introHtml: string;
    emailBodyBlock: string;
    fileUrl: string;
    openButtonLabel: string;
}): string => {
    const fileUrlSafe = escapeHtml(params.fileUrl);
    const openButtonSafe = escapeHtml(params.openButtonLabel);

    return `<div style="font-family: Arial, sans-serif; color: #1f2937; line-height: 1.55;">
  <p style="margin: 0 0 12px;">${escapeHtml(tEmail('greeting'))}</p>
  ${params.emailBodyBlock}
  <p style="margin: 0 0 12px;">${params.introHtml}</p>
  <p style="margin: 0 0 16px;">${escapeHtml(tEmail('attachedHint'))}</p>
  <p style="margin: 0 0 16px;">
    <a href="${fileUrlSafe}" target="_blank" rel="noreferrer" style="display: inline-block; background: #2563eb; color: #ffffff; text-decoration: none; border-radius: 6px; padding: 10px 14px;">${openButtonSafe}</a>
  </p>
  <p style="margin: 0 0 12px; font-size: 12px; color: #4b5563;">${escapeHtml(tEmail('linkFallbackHint'))}</p>
  <p style="margin: 0 0 18px; font-size: 12px; word-break: break-all;"><a href="${fileUrlSafe}" target="_blank" rel="noreferrer">${fileUrlSafe}</a></p>
  <p style="margin: 0;">${escapeHtml(tEmail('regards'))}<br />${escapeHtml(tEmail('team'))}</p>
</div>`;
};

export const buildDocumentMailContent = async (params: {
    type?: string | null;
    documentName: string;
    fileUrl: string;
    emailBody?: string | null;
}): Promise<DocumentMailContentType | null> => {
    await ensureEmailNamespace();

    const fileUrl = String(params.fileUrl || '').trim();
    const documentName = String(params.documentName || '').trim();
    if (!fileUrl || !documentName) return null;

    const type = resolveDocumentType(params.type);
    const subject = tEmail(`documents.${type}.subject`);
    const documentLabel = tEmail(`documents.${type}.label`);
    const introHtml = `${escapeHtml(tEmail('sendIntroPrefix'))} ${escapeHtml(documentLabel)} <strong>${escapeHtml(documentName)}</strong>${escapeHtml(tEmail('sendIntroSuffix'))}`;
    const openButtonLabel = tEmail('openButton', { documentLabel });

    return {
        subject,
        html: buildMailShell({
            introHtml,
            emailBodyBlock: buildEmailBodyBlock(params.emailBody),
            fileUrl,
            openButtonLabel,
        }),
    };
};

export const buildSupplierRequestMailContent = async (params: {
    fileUrl: string;
    emailBody?: string | null;
}): Promise<DocumentMailContentType | null> => {
    await ensureEmailNamespace();

    const fileUrl = String(params.fileUrl || '').trim();
    if (!fileUrl) return null;

    return {
        subject: tEmail('supplierRequest.subject'),
        html: buildMailShell({
            introHtml: escapeHtml(tEmail('supplierRequest.intro')),
            emailBodyBlock: buildEmailBodyBlock(params.emailBody),
            fileUrl,
            openButtonLabel: tEmail('supplierRequest.openButton'),
        }),
    };
};
